// import axios from "axios";
// import { jwtDecode } from "jwt-decode";

// const instance = axios.create({
//   baseURL: process.env.REACT_APP_SERVER_URL,
//   headers: { "Content-Type": "application/json" },
// });

// /* 🔁 Refresh token orchestration helpers */
// let isRefreshing = false;
// let failedQueue = [];

// const processQueue = (error, token = null) => {
//   failedQueue.forEach(({ resolve, reject }) =>
//     error ? reject(error) : resolve(token)
//   );
//   failedQueue = [];
// };

// /* 📨 REQUEST interceptor */
// instance.interceptors.request.use(async (config) => {
//   console.log("%c[AXIOS] ↗️  Outgoing request:", "color:#00bcd4", config.url);

//   const token = localStorage.getItem("accessToken");

//   if (!token) {
//     console.warn("%c[AXIOS] ⚠️  No accessToken in LS", "color:gray");
//     return config; // guest request
//   }

//   /* 1️⃣ Expiry check */
//   const { exp } = jwtDecode(token);
//   const expired = Date.now() > exp * 1000;
//   console.log(
//     `%c[AXIOS] 🕒 Token expired? ${expired}`,
//     `color:${expired ? "red" : "green"}`
//   );

//   if (!expired) {
//     config.headers.Authorization = `Bearer ${token}`;
//     return config;
//   }

//   /* 2️⃣ If expired → refresh logic */
//   if (isRefreshing) {
//     console.log(
//       "%c[AXIOS] ⏳ Waiting for refresh to finish…",
//       "color:#ffc107"
//     );
//     return new Promise((resolve, reject) => {
//       failedQueue.push({
//         resolve: (newTok) => {
//           config.headers.Authorization = `Bearer ${newTok}`;
//           resolve(config);
//         },
//         reject,
//       });
//     });
//   }

//   isRefreshing = true;
//   const refreshToken = localStorage.getItem("refreshToken");
//   console.log("%c[AXIOS] 🔁  Hitting /users/accesstoken", "color:#ff9800");

//   try {
//     const { data } = await axios.post(
//       `${process.env.REACT_APP_SERVER_URL}/users/accesstoken`,
//       { refreshToken }
//     );
//     const newAccessToken = data.accessToken;

//     console.log(
//       "%c[AXIOS] ✅  New token received:",
//       "color:#4caf50",
//       newAccessToken?.slice(0, 25) + "..."
//     );

//     localStorage.setItem("accessToken", newAccessToken);
//     processQueue(null, newAccessToken);

//     config.headers.Authorization = `Bearer ${newAccessToken}`;
//     return config;
//   } catch (err) {
//     console.error("%c[AXIOS] ❌  Refresh failed - logging out", "color:red");
//     processQueue(err, null);
//     localStorage.clear();
//     window.location.href = "/login";
//     return Promise.reject(err);
//   } finally {
//     isRefreshing = false;
//   }
// });
// export default instance;
import axios from "axios";
import { jwtDecode } from "jwt-decode";

/* ------------------------------------------------------------------ */
/* 🌐  Base instance                                                  */
/* ------------------------------------------------------------------ */
const instance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  headers: { "Content-Type": "application/json" },
});

/* ------------------------------------------------------------------ */
/* 🔁  Refresh helpers                                                */
/* ------------------------------------------------------------------ */
let isRefreshing = false;
let failedQueue  = [];

const processQueue = (err, token = null) => {
  failedQueue.forEach(({ resolve, reject }) =>
    err ? reject(err) : resolve(token)
  );
  failedQueue = [];
};

const slice = (tok) => (tok ? `${tok.slice(0, 8)}…${tok.slice(-8)}` : "undef");

const REFRESH_ENDPOINT = "/users/accesstoken";

const extractToken = (data) =>
  data?.accessToken ?? data?.details?.accessToken ?? data?.token;

/* ------------------------------------------------------------------ */
/* 📤  REQUEST interceptor                                            */
/* ------------------------------------------------------------------ */
instance.interceptors.request.use(async (config) => {
  const currTok = localStorage.getItem("accessToken");
  console.log(
    `%c[AXIOS] ↗️ ${config.method?.toUpperCase()} ${config.url} | tok ${slice(
      currTok
    )}`,
    "color:#00bcd4"
  );

  /* guest request — just continue */
  if (!currTok) return config;

  /* 1️⃣ expiry check */
  let expired = false;
  try {
    expired = Date.now() > jwtDecode(currTok).exp * 1000;
  } catch {
    expired = true;
  }
  console.log(
    `%c[AXIOS] 🕒 expired? ${expired}`,
    `color:${expired ? "red" : "green"}`
  );

  if (!expired) {
    config.headers.Authorization = `Bearer ${currTok}`;
    return config; // token still fresh
  }

  /* 2️⃣ token expired → refresh flow */
  if (isRefreshing) {
    return new Promise((resolve, reject) =>
      failedQueue.push({
        resolve: (tok) => {
          console.log(
            `%c[AXIOS] ⏩ queued req uses tok ${slice(tok)}`,
            "color:#03a9f4"
          );
          config.headers.Authorization = `Bearer ${tok}`;
          resolve(config);
        },
        reject,
      })
    );
  }

  /* single refresher */
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    localStorage.clear();
    window.location.href = "/login";
    throw new Error("refreshToken missing");
  }

  isRefreshing = true;
  console.warn(
    `%c[AXIOS] 🔁 POST ${REFRESH_ENDPOINT} | old ${slice(currTok)}`,
    "color:#ff9800"
  );

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}${REFRESH_ENDPOINT}`,
      { refreshToken }
    );

    const newTok = extractToken(data);
    if (!newTok) {
      console.error("[AXIOS] ⛔ refresh response missing accessToken", data);
      throw new Error("No accessToken in refresh response");
    }

    console.log(`%c[AXIOS] ✅ new tok ${slice(newTok)}`, "color:#4caf50");

    localStorage.setItem("accessToken", newTok);
    processQueue(null, newTok);

    config.headers.Authorization = `Bearer ${newTok}`;
    return config;
  } catch (err) {
    console.error("%c[AXIOS] ❌ refresh failed – logout", "color:red");
    processQueue(err, null);
    localStorage.clear();
    window.location.href = "/login";
    throw err;
  } finally {
    isRefreshing = false;
  }
});

/* ------------------------------------------------------------------ */
/* 🛡️  RESPONSE interceptor — **only** forward errors                 */
/* ------------------------------------------------------------------ */
instance.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err)
);

export default instance;
